import React, { useEffect, useState } from 'react';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import Grid from '@mui/material/Grid';
import { useForm } from 'react-hook-form';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { SelectedFilters } from 'components/organisms/SelectedFilters';
import { FormSelect } from 'components/molecules/FormSelect';
import { prepareFilters } from 'helpers/filters';
import { useTranslations } from 'hooks/useTranslations';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { mapDictionaryToOptionProp } from 'helpers/dictionary';
import { FormInput } from 'components/molecules/FormInput';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { ILicencesSearchForm } from 'models/licences';
import { useFilterPresets } from 'hooks/useFilterPresets';
import { ESearchFilterKeys, ESearchFilterTypes } from 'constants/SearchFilterTypes';
import { formatDateTimeToIso, formatDateToRequest } from 'utils/date';
import { ERouteLinks } from 'models/route';
import { getSearchFilter } from 'requests/searchFilter';
import { Box } from '@mui/material';
import { EPerPages } from 'models/table';
import { isEmptyObject } from 'pages/AncillariesPage/helpers';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useLicences } from '../hooks';
import { getStatusDataOptions } from '../helpers';
import { ESearchFormLabels } from '../../../constants/SearchFormLabelsTypes';
import { ISearchParams } from '../licences';
import { StyledAdvancedSearch, StyledPresetSelect } from './styles';
import { useThemeBreakpoint } from '../../../hooks/useThemeBreakpoint';
import { FormMultiSelect } from 'components/molecules/FormMultiSelect';
import { multiFieldsList } from 'helpers/multiFieldsList';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import FormCustomField from 'components/organisms/FormCustomField';
import { ISearchFormCustomFieldValue } from 'pages/LegalEntitiesPage/IndividualSearchForm';

const customFieldWrapper = (children: React.ReactNode) => (
  <Grid size={{ xs: 12, md: 3 }}>{children}</Grid>
);
interface ILicenceSearchForm {
  onSubmitForm: (arg: ILicencesSearchForm) => void;
  initialSearchForm: ILicencesSearchForm;
  isSearchButtonDisabled?: boolean;
  isSearchParams: boolean;
  getNewQueryPath?: (arg: any) => string;
  currentPage: number;
  perPage: EPerPages;
  customFieldsLicence: ICustomFieldByObjectType[];
}

const LicenceSearchForm = ({
  onSubmitForm,
  initialSearchForm,
  isSearchParams,
  isSearchButtonDisabled = false,
  getNewQueryPath,
  perPage,
  currentPage,
  customFieldsLicence,
}: ILicenceSearchForm) => {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    getValues,
    watch,
    formState: { errors },
  } = useForm<ILicencesSearchForm>({
    defaultValues: initialSearchForm,
  });
  const [isInitialFormLoad, setIsInitialFormLoad] = useState(true);
  const [values, setValues] = useState<ILicencesSearchForm>(initialSearchForm);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(isSearchParams);
  const [customFields, setCustomFields] = useState<ICustomFieldByObjectType[]>([]);
  const { t } = useTranslations();
  const { state: routerState }: any = useLocation();
  const { selectedLanguage } = useLang();
  const { loadDictionaries, legalType, weaponLicencingAuthority } = useLicences();
  const { getFilterPresets, presets, getPresetValues, preparePresetsList } =
    useFilterPresets();
  const dateKeys = ['expiryDateFrom', 'expiryDateTo'];
  const { isDesktop } = useThemeBreakpoint();

  const onSubmit = (data: ILicencesSearchForm) => {
    onSubmitForm(prepareData(data));
  };

  const prepareData = (data: ILicencesSearchForm): ILicencesSearchForm => {
    const customFieldsPayload: ISearchFormCustomFieldValue[] = [];

    Object.keys(data).map((key) => {
      const isDateCF = key.startsWith('from_') || key.startsWith('to_');

      if (!isDateCF) {
        const customField = customFieldsLicence.find((item) => item.name === key);
        if (customField && data[key]) {
          const customFieldItem: ISearchFormCustomFieldValue = {
            customFieldUniqueId: customField.uniqueId,
            customFieldType: customField.customFieldType,
            name: customField.name,
          };
          if (customField.customFieldType === ECustomFieldType.TEXT) {
            if (data[key] === undefined || data[key] === null) {
              customFieldItem.text = undefined;
            } else if (data[key].trim() === '') {
              customFieldItem.text = undefined;
            } else {
              customFieldItem.text = data[key];
            }
          } else if (customField.customFieldType === ECustomFieldType.BOOL) {
            customFieldItem.bool = data[key] === '1';
          }

          customFieldsPayload.push(customFieldItem);
          delete data[key];
        }
      } else {
        const customFieldKey = key.startsWith('from_')
          ? key.replace('from_', '')
          : key.replace('to_', '');

        const customField = customFieldsLicence.find((item) => item.name === customFieldKey);

        if (customField && (data[`from_${customFieldKey}`] || data[`to_${customFieldKey}`])) {
          const customFieldItem: ISearchFormCustomFieldValue = {
            customFieldUniqueId: customField.uniqueId,
            customFieldType: customField.customFieldType,
            name: customField.name,
          };
          if (data[`from_${customFieldKey}`]) {
            customFieldItem.dateFrom =
              data[`from_${customFieldKey}`] !== null
                ? formatDateTimeToIso(data[`from_${customFieldKey}`])
                : formatDateTimeToIso(new Date().toString());
          }
          if (data[`to_${customFieldKey}`]) {
            customFieldItem.dateTo =
              data[`to_${customFieldKey}`] !== null
                ? formatDateTimeToIso(data[`to_${customFieldKey}`])
                : formatDateTimeToIso(new Date().toString());
          }
          customFieldsPayload.push(customFieldItem);
          if (data[`from_${customFieldKey}`]) {
            delete data[`from_${customFieldKey}`];
          }
          if (data[`to_${customFieldKey}`]) {
            delete data[`to_${customFieldKey}`];
          }
        }
      }
      return false;
    });

    if (customFieldsPayload?.length) {
      data.customFields = customFieldsPayload;
    } else {
      delete data.customFields;
    }
    return data;
  };

  useEffect(() => {
    if (!isInitialFormLoad) {
      if (getNewQueryPath) {
        handleSubmit((val) => onSubmit({ ...val, invokedByPageChange: true }))();
      }
    } else {
      setIsInitialFormLoad(false);
    }
  }, [perPage, currentPage]);

  const [searchParams] = useSearchParams();
  const presetId =
    searchParams.get('presetId') || searchParams.get('preset') || routerState?.presetLicence;
  const watchPreset = watch('preset');

  const setPresetFilters = async (
    selectedPreset: string,
    isFromEmailLink: boolean = false,
    isClickedManually: boolean = false,
    isInitialLoad: boolean = false,
  ) => {
    const handleValues = async () => {
      if (isFromEmailLink) {
        const filter = await getSearchFilter(selectedPreset);
        if (filter) {
          const queryValues: ISearchParams = {};
          filter.values.forEach(({ key, value }) => {
            if (multiFieldsList.includes(key)) {
              if (!Array.isArray(queryValues[key])) {
                queryValues[key] = [];
              }
              (queryValues[key] as string[]).push(value);
            } else if (dateKeys.includes(key)) {
              queryValues[key] = new Date(formatDateToRequest(value as string));
            } else {
              queryValues[key] = value;
            }
          });
          return queryValues;
        }
      }
      return getPresetValues(selectedPreset);
    };

    const initialValues =
      isInitialLoad && !routerState?.fromSelectLE
        ? { ...initialSearchForm, ...routerState?.formValues }
        : initialSearchForm;

    const presetValues: any = {
      ...(isClickedManually ? initialSearchForm : initialValues),
      preset: selectedPreset,
      ...(await handleValues()),
    };

    setValues(presetValues);
    reset(presetValues);

    if (isClickedManually) {
      await handleSubmit(onSubmit)();
    }
  };

  useEffect(() => {
    const sub = watch((val) => {
      const updatedValues: any = { ...val };
      dateKeys.forEach((key: string) => {
        if (updatedValues[key]) {
          updatedValues[key] = new Date(updatedValues[key]);
        }
      });

      setValues(updatedValues as ILicencesSearchForm);
    });
    return () => sub.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (initialSearchForm) reset(initialSearchForm);
  }, [initialSearchForm, reset]);

  useEffect(() => {
    (async function init() {
      await getFilterPresets(ESearchFilterTypes.licences);
      if (presetId) {
        await setPresetFilters(presetId, true);
        setShowAdvanced(true);
        await handleSubmit(onSubmit)();
      }

      if (routerState?.formValues) {
        const formValues = {
          ...initialSearchForm,
          ...routerState.formValues,
          legalityUniqueId: routerState?.legalityUniqueId,
        };
        setValues(formValues);
        reset(formValues);
      }
      if (!isEmptyObject(routerState?.formValues)) {
        setShowAdvanced(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async function init() {
      if (watchPreset) {
        await setPresetFilters(watchPreset, false, false, true);
      } else {
        await setPresetFilters('', false, false, true);
      }
      await handleSubmit(onSubmit)();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialSearchForm) reset(initialSearchForm);
  }, [initialSearchForm, reset]);

  useEffect(() => {
    (async function init() {
      await getFilterPresets(ESearchFilterTypes.licences);
      await loadDictionaries([
        EDictionaryTypes.legalType,
        EDictionaryTypes.weaponLicencingAuthority,
      ]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    setCustomFields(
      customFieldsLicence.filter((item: ICustomFieldByObjectType) => item.searchCriteria),
    );
  }, [customFieldsLicence]);

  const handleResetField = (name?: string, refreshResults: boolean = true) => {
    const currentValues = getValues();
    if (name) {
      reset({ ...currentValues, [name]: '' });
    } else {
      const formValues = { ...getValues() };
      Object.keys(formValues).map((key: string) => {
        if (typeof values[key] === 'string') {
          values[key] = '';
        } else if (typeof values[key] === 'object') {
          values[key] = null;
        }
        return false;
      });
      reset(values);
    }

    if (refreshResults) {
      onSubmit(getValues());
    }
  };

  return (
    <SearchWrapper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid
          container
          spacing={3}
          sx={{ mb: { xs: 2, md: 0 }, justifyContent: 'space-between' }}
        >
          <Grid size={{ xs: 12, md: 8 }}>
            <StyledPresetSelect
              options={presets ? preparePresetsList(presets) : []}
              name="preset"
              control={control}
              setValue={(name: string, value: string) => {
                setValue('preset', value);
                handleResetField();
              }}
              onSelect={(val: any) => {
                if (!val) {
                  handleResetField();
                } else {
                  setPresetFilters(val, false, true);
                }
              }}
              errors={errors}
              withValidation={false}
              withLabel={false}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <Button
              variant={EButtonVariants.outlined}
              label={t('licencesList.advancedFilters.button')}
              onClick={() => setShowAdvanced(!showAdvanced)}
              size={EButtonSizes.small}
              fullWidth
            />
          </Grid>
        </Grid>
        {showAdvanced && (
          <StyledAdvancedSearch container columnSpacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="weaponLicenceNumber"
                label={t('licences.weaponLicenceNumber.label')}
                setValue={setValue}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="weaponLicenceName"
                label={t('licences.licenceName.label')}
                setValue={setValue}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormInput
                charsLimit={120}
                maxLength
                name="legalName"
                label={t('licences.legalName.label')}
                setValue={setValue}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormSelect
                options={mapDictionaryToOptionProp(legalType)}
                name="legalTypeUniqueId"
                setValue={setValue}
                canBeEmpty
                label={t('licences.legalType.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormMultiSelect
                options={getStatusDataOptions(t)}
                name="statusesUniqueIds"
                setValue={setValue}
                canBeEmpty
                label={t('licences.status.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormMultiSelect
                options={mapDictionaryToOptionProp(weaponLicencingAuthority)}
                name="licenceAuthorityUniqueIds"
                setValue={setValue}
                canBeEmpty
                label={t('licences.licencingAuthority.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormDatePicker
                name="expiryDateFrom"
                label={t('licences.expiryDateFrom.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <FormDatePicker
                name="expiryDateTo"
                label={t('licences.expiryDateTo.label')}
                control={control}
                errors={errors}
              />
            </Grid>
            {customFields.map((customField) => (
              <FormCustomField
                data={customField}
                control={control}
                setValue={setValue}
                errors={errors}
                key={customField.uniqueId}
                wrapper={customFieldWrapper}
                isSearch={true}
              />
            ))}
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}
            >
              <Button
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                disabled={isSearchButtonDisabled}
                label={t('licences.search')}
                type="submit"
                sx={{ width: { xs: '100%', md: '25%' } }}
              />
            </Grid>
          </StyledAdvancedSearch>
        )}
      </form>
      <SelectedFilters
        name="licences"
        values={values}
        excludes={['preset', 'perPage', 'currentPage', 'customFields']}
        customFields={customFields}
        labels={ESearchFormLabels}
        updatedQueryPath={
          getNewQueryPath ? getNewQueryPath(prepareData(getValues())) : undefined
        }
        handleDelete={handleResetField}
        type={ESearchFilterKeys.licences}
        dictionaries={{
          statusesUniqueIds: prepareFilters(getStatusDataOptions(t), 'value', 'label'),
          legalTypeUniqueId: prepareFilters(legalType, 'uniqueId', 'name'),
          licenceAuthorityUniqueIds: prepareFilters(
            weaponLicencingAuthority,
            'uniqueId',
            'name',
          ),
        }}
        saveRouterStateAfterClearAll={false}
        saveRoute={ERouteLinks.SaveLicencesPreset.replace(':type', ESearchFilterKeys.licences)}
      />
    </SearchWrapper>
  );
};

export { LicenceSearchForm };
